import { useEffect, useMemo } from 'react';
import type React from 'react';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';
import { LayerGroupDto, LayerNodeDto } from 'hooks/layerNode/types';
import {
  ComboboxGroupedField,
  type ComboboxGroupedFieldGroup,
} from 'components/DesignSystems/ComboboxGroupedField/ComboboxGroupedField';

type CategorySelectProps = {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  incidentId: string | null;
};

const GLOBAL_GROUP_KEY = '__global__';

function hasFolder(
  group: LayerGroupDto
): group is LayerGroupDto & { folder: LayerNodeDto } {
  return group.folder != null;
}

export const CategorySelect: React.FC<CategorySelectProps> = ({
  label,
  value,
  onChange,
  incidentId,
}) => {
  const groupGuid = useGroupGuid();
  const { data } = useListLayerNodes(
    { filter: { groupGuid } },
    { enabled: Boolean(groupGuid) }
  );

  const folders = useMemo(
    () => (data?.data ?? []).filter(hasFolder).map((group) => group.folder),
    [data?.data]
  );

  const groups = useMemo(() => {
    const byKey = new Map<string, ComboboxGroupedFieldGroup>();

    for (const folder of folders) {
      const key = folder.incidentId ?? GLOBAL_GROUP_KEY;
      let group = byKey.get(key);
      if (!group) {
        group = {
          key,
          label: folder.incidentId
            ? (folder.incident?.name ?? folder.incidentId)
            : 'Global',
          items: [],
          disabled: !!incidentId && folder.incidentId !== incidentId,
        };
        byKey.set(key, group);
      }

      group.items.push({ value: folder.id, label: folder.name });
    }

    return [...byKey.values()].sort();
  }, [folders, incidentId]);

  // Reset the selected value if it no longer belongs to the current incident.
  useEffect(() => {
    if (!value || !incidentId) return;
    const selected = folders.find((folder) => folder.id === value);
    if (selected && selected.incidentId !== incidentId) {
      onChange(null);
    }
  }, [value, incidentId, folders, onChange]);

  return (
    <ComboboxGroupedField
      label={label}
      groups={groups}
      emptyMessage="No folders found."
      value={value}
      onChange={(next) => onChange(Array.isArray(next) ? null : next)}
    />
  );
};
